import {
  Box,
  Checkbox,
  Flex,
  useDisclosure,
  useToast
} from '@chakra-ui/react';
import React, { useState } from 'react';
import { serverRequest } from '../../api/Api';
import { OutlineButton, PrimaryButton } from '../Buttons/Buttons';
import { TextField } from '../Inputs/TextField';
import ModalView from '../Modal';
import { Body, Headline } from '../Texts/Texts';
import { colors } from '../../theme/colors';
import { UserRole } from '../../constants/roles';

export const isCaseSafetyFlagged = (caseItem: any): boolean => {
  if (!caseItem) {
    return false;
  }
  return (
    caseItem.isAtRisk ||
    caseItem.isReported ||
    caseItem.status === 'ESCALATED' ||
    caseItem.riskState?.isHighRisk
  );
};

interface SafetyCasePanelProps {
  caseItem: any;
  caseId: string;
  currentRole: UserRole | null;
  onUpdated: () => void;
}

const SafetyCasePanel = ({
  caseItem,
  caseId,
  currentRole,
  onUpdated
}: SafetyCasePanelProps) => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isTerminateOpen,
    onOpen: onTerminateOpen,
    onClose: onTerminateClose
  } = useDisclosure();
  const [loading, setLoading] = useState(false);

  const [threatSummary, setThreatSummary] = useState('');
  const [actionsTaken, setActionsTaken] = useState('');
  const [supervisorNotified, setSupervisorNotified] = useState(true);
  const [clientSafetyPlan, setClientSafetyPlan] = useState('');
  const [emergencyServicesContacted, setEmergencyServicesContacted] =
    useState(false);
  const [terminateReason, setTerminateReason] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');

  const isSuperAdmin = currentRole === UserRole.SUPER_ADMIN;
  const isSupervisor = currentRole === UserRole.SUPERVISOR;
  const isAdmin =
    currentRole === UserRole.ADMIN || currentRole === UserRole.SUPER_ADMIN;

  if (!isCaseSafetyFlagged(caseItem)) {
    return null;
  }

  const submitSafetyReview = async () => {
    if (!threatSummary.trim() || !actionsTaken.trim()) {
      toast({
        title: 'Error',
        description: 'Threat summary and actions taken are required',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    setLoading(true);
    try {
      await serverRequest({
        endpoint: `cases/${caseId}/safety-review`,
        method: 'PUT',
        body: {
          threatSummary,
          actionsTaken,
          supervisorNotified,
          clientSafetyPlan: clientSafetyPlan || undefined,
          emergencyServicesContacted
        }
      });
      onClose();
      onUpdated();
      toast({
        title: 'Success',
        description: 'Safety review submitted',
        status: 'success',
        duration: 3000,
        isClosable: true
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to submit safety review',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    } finally {
      setLoading(false);
    }
  };

  const reviewApproval = async (status: 'APPROVED' | 'REJECTED') => {
    setLoading(true);
    try {
      await serverRequest({
        endpoint: `cases/${caseId}/closure-approval`,
        method: 'PUT',
        body: { status, reviewNotes: approvalNotes || undefined }
      });
      onUpdated();
      toast({
        title: 'Success',
        description:
          status === 'REJECTED'
            ? 'Closure rejected — case has been reopened'
            : 'Closure approved',
        status: 'success',
        duration: 3000,
        isClosable: true
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to update closure approval',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    } finally {
      setLoading(false);
    }
  };

  const terminateCase = async () => {
    if (!terminateReason.trim() || !threatSummary.trim() || !actionsTaken.trim()) {
      toast({
        title: 'Error',
        description: 'All termination and safety review fields are required',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    setLoading(true);
    try {
      await serverRequest({
        endpoint: `cases/${caseId}/terminate`,
        method: 'PUT',
        body: {
          reason: terminateReason,
          threatSummary,
          actionsTaken,
          supervisorNotified,
          clientSafetyPlan: clientSafetyPlan || undefined,
          emergencyServicesContacted,
          reviewNotes: approvalNotes || undefined
        }
      });
      onTerminateClose();
      onUpdated();
      toast({
        title: 'Success',
        description: 'Case terminated',
        status: 'success',
        duration: 3000,
        isClosable: true
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to terminate case',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    } finally {
      setLoading(false);
    }
  };

  const approvalStatus = caseItem.closureApproval?.status || 'PENDING';
  const reviewComplete = Boolean(caseItem.safetyReview?.completedAt);

  return (
    <Box
      mt='24px'
      p='20px'
      borderRadius='8px'
      bg='rgba(255, 113, 113, 0.08)'
      border='1px solid #FF7171'
    >
      <Body size='16px' color='#FF7171' fontWeight={700} mb='8px'>
        Safety-Flagged Case
      </Body>
      {caseItem.autoClosedDueToSafety && (
        <Body size='14px' color='gray' mb='8px'>
          Automatically closed due to safety escalation. Super Admin review
          required.
        </Body>
      )}
      <Body size='14px' color='gray' mb='12px'>
        Approval status: {approvalStatus}
        {reviewComplete ? ' · Safety review on file' : ' · Safety review required'}
      </Body>

      <Flex gap='8px' flexWrap='wrap' mb='12px'>
        {caseItem.stage !== 'CLOSED' && (
          <OutlineButton
            label='Submit Safety Review'
            onClick={onOpen}
            height='44px'
            color={colors.deep_blue}
          />
        )}
        {(isAdmin || isSupervisor) && caseItem.stage !== 'CLOSED' && (
          <OutlineButton
            label='Terminate Case'
            onClick={onTerminateOpen}
            height='44px'
            color='#FF7171'
          />
        )}
      </Flex>

      {(isSuperAdmin || isSupervisor) && (
        <Flex gap='8px' flexWrap='wrap'>
          <PrimaryButton
            label='Approve Closure'
            onClick={() => reviewApproval('APPROVED')}
            height='44px'
            isLoading={loading}
            background='deep_blue'
          />
          <OutlineButton
            label='Reject Closure'
            onClick={() => reviewApproval('REJECTED')}
            height='44px'
            color='#FF7171'
          />
        </Flex>
      )}

      {(isSuperAdmin || isSupervisor) && (
        <Box mt='12px'>
          <TextField
            label='Approval notes'
            value={approvalNotes}
            onChange={setApprovalNotes}
            bg='white'
          />
        </Box>
      )}

      <ModalView isOpen={isOpen} onClose={onClose} maxW='600px' bg='gray_50'>
        <Box py='40px' px='50px'>
          <Headline size='19px' color='deep_blue' mb='20px'>
            Safety Review Form
          </Headline>
          <TextField
            label='Threat summary *'
            value={threatSummary}
            onChange={setThreatSummary}
            bg='white'
          />
          <TextField
            label='Actions taken *'
            value={actionsTaken}
            onChange={setActionsTaken}
            bg='white'
          />
          <TextField
            label='Client safety plan'
            value={clientSafetyPlan}
            onChange={setClientSafetyPlan}
            bg='white'
          />
          <Checkbox
            isChecked={supervisorNotified}
            onChange={(e) => setSupervisorNotified(e.target.checked)}
            mb='12px'
          >
            Supervisor notified
          </Checkbox>
          <Checkbox
            isChecked={emergencyServicesContacted}
            onChange={(e) => setEmergencyServicesContacted(e.target.checked)}
            mb='20px'
          >
            Emergency services contacted
          </Checkbox>
          <PrimaryButton
            label='Submit'
            onClick={submitSafetyReview}
            isLoading={loading}
            width='100%'
          />
        </Box>
      </ModalView>

      <ModalView
        isOpen={isTerminateOpen}
        onClose={onTerminateClose}
        maxW='600px'
        bg='gray_50'
      >
        <Box py='40px' px='50px'>
          <Headline size='19px' color='deep_blue' mb='20px'>
            Terminate Safety Case
          </Headline>
          <TextField
            label='Termination reason *'
            value={terminateReason}
            onChange={setTerminateReason}
            bg='white'
          />
          <TextField
            label='Threat summary *'
            value={threatSummary}
            onChange={setThreatSummary}
            bg='white'
          />
          <TextField
            label='Actions taken *'
            value={actionsTaken}
            onChange={setActionsTaken}
            bg='white'
          />
          <PrimaryButton
            label='Terminate'
            onClick={terminateCase}
            isLoading={loading}
            width='100%'
            background='#FF7171'
          />
        </Box>
      </ModalView>
    </Box>
  );
};

export default SafetyCasePanel;
